import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import generatePdf from "@tSurya/Handlers/GeneratePdfHandler";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
         location : getLocation(state),
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
    };
};

const mapDispatchToProps = {
    generatePdf,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    returnButton?: ButtonPresentationProps;
    returnRequest?: InjectableCss;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    buttonWrapper?: InjectableCss;
    mobileCenteringWrapper?: InjectableCss;
    mobileSpinner?: LoadingSpinnerProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    returnButton: {
        css: css`
            margin-left: 10px;
            padding: 10px 34px;
            > span {
                color: ${getColor("primary.contrast")};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }

            &:hover {
                background: ${getColor("text.accent")};
                span[class^="TypographyStyle"] {
                    color: ${getColor("secondary")};
                }
            }
        `,
    },
    returnRequest: {
        css: css`
            display: inline-block;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 40px;
            width: 100%;
            display: flex;
            align-items: center;
            padding: 0px 30px;
        `,
    },
    spinner: {
        size: 30,
        css: css`
            margin: auto;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
        `,
    },
    mobileCenteringWrapper: {
        css: css`
            padding: 10px 15px;
        `,
    },
    mobileSpinner: {
        size: 24,
        css: css`
            margin: auto;
        `,
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<Props> = ({ fields, rmaLink, history, generatePdf }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const toasterContext = React.useContext(ToasterContext);
    const [isLoading, setIsLoading] = useState(false);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }

    const onClickGeneratePdf = () => {
        setIsLoading(true);
        generatePdf({
            identifier: invoice.invoiceNumber,
            templateName: "invoice",
            onSuccess(result) {
                setIsLoading(false);
            },
            onError(result) {
                setIsLoading(false);
                toasterContext.addToast({
                    body: translate(`${result}`),
                    messageType: "danger",
                });
            },
        });
    };
    const orderNo = invoice.properties.erpOrderNo;

    const printLabel = translate("Print");

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice")} # ${invoice.invoiceNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    {
                        !isLoading &&(

                            <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                            {/* commenting these lines because we not trigger Email as a phase 1 of project */}
    
                            {/* <ShareEntityButton
                                entityId={invoice.invoiceNumber}
                                variant="clickable"
                                entityName="Invoice"
                                extendedStyles={styles.shareEntityButtonStyles}
                                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                            /> */}
                            {!isLoading ? (
                                <Clickable
                                    {...styles.printClickable}
                                    onClick={onClickGeneratePdf}
                                    data-test-selector="invoiceDetails_print"
                                    disabled={isLoading}
                                >
                                   {printLabel}
                                </Clickable>
                            ) : (
                                <StyledWrapper {...styles.mobileCenteringWrapper}>
                                    <LoadingSpinner {...styles.mobileSpinner} />
                                </StyledWrapper>
                            )}
                           { orderNo && <Clickable
                                {...styles.printClickable}
                                href={`${rmaLink?.url}?orderNumber=${orderNo}&invoiceNumber=${invoice.invoiceNumber}&invoiceDate=${invoice.invoiceDate}&PoNumber=${invoice.customerPO}`}
                                data-test-selector="invoiceDetails_print"
                            >
                                Return
                            </Clickable>}
                        </OverflowMenu>
                        )
                    }
                   
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <StyledWrapper {...styles.buttonWrapper}>
                        {/* <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    /> */}
                        {!isLoading &&(
                            <Button
                                {...styles.printButton}
                                onClick={onClickGeneratePdf}
                                data-test-selector="invoiceDetails_print"
                                disabled={isLoading}
                            >
                                {printLabel}
                            </Button>
                        )}
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {orderNo ? (
                            <Clickable
                                {...styles.returnRequest}
                                href={`${rmaLink?.url}?orderNumber=${orderNo}&invoiceNumber=${invoice.invoiceNumber}&invoiceDate=${invoice.invoiceDate}&PoNumber=${invoice.customerPO}`}
                            >
                                <Button {...styles.returnButton} variant="primary">
                                    Return
                                </Button>
                            </Clickable>
                        ) : null}
                    </StyledWrapper>
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(InvoiceDetailHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};
export default widgetModule;
